import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type SupportedLanguage = 'de' | 'en';

interface TranslationDictionary {
  [key: string]: string;
}

interface I18nContextValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: string) => string;
}

const STORAGE_KEY = 'dashboard_language';

const translations: Record<SupportedLanguage, TranslationDictionary> = {
  de: {
    'common.save': 'Speichern',
    'common.saveAll': 'Alles speichern',
    'common.saving': 'Speichert...',
    'common.loading': 'Lädt...',
    'common.clear': 'Löschen',
    'common.menu': 'Menü',
    'common.logout': 'Abmelden',
    'common.language': 'Sprache',
    'common.theme': 'Design',
    'common.system': 'System',
    'common.add': 'Hinzufügen',
    'common.edit': 'Bearbeiten',
    'common.delete': 'Löschen',
    'common.actions': 'Aktionen',
    'common.search': 'Suchen',
    'common.status': 'Status',
    'common.loadingData': 'Daten werden geladen...',
    'common.noData': 'Keine Daten verfügbar',
    'common.notAvailable': 'Nicht verfügbar',
    'common.online': 'Online',
    'common.offline': 'Offline',
    'common.view': 'Ansehen',
    'common.never': 'Nie',
    'common.lastUpdate': 'Letztes Update',
    'common.balance': 'Kontostand',
    'common.profit': 'Profit',
    'common.strategy': 'Strategie',
    'common.exchange': 'Exchange',
    'common.bots': 'Bots',
    'common.trades': 'Trades',
    'common.open': 'Offen',
    'common.winRate': 'Gewinnrate',
    'common.environment': 'Umgebung',
    'common.refreshing': 'Aktualisiert...',
    'common.scanNow': 'Jetzt scannen',
    'common.sortBy': 'Sortieren nach',
    'common.hide': 'Ausblenden',
    'common.more': 'Mehr',
    'nav.dashboard': 'Dashboard',
    'nav.freqtradeBots': 'Freqtrade Bots',
    'nav.historicBots': 'Historische Bots',
    'nav.financialData': 'Finanzdaten',
    'nav.strategyLab': 'Strategy Lab',
    'nav.pairlistOptimizer': 'Pairlist Optimizer',
    'nav.backtestResults': 'Backtest-Ergebnisse',
    'nav.compare': 'Vergleichen',
    'nav.discovery': 'Discovery',
    'nav.alerts': 'Alerts',
    'nav.settings': 'Einstellungen',
    'nav.users': 'Benutzer',
    'tabs.overview': 'Übersicht',
    'tabs.trades': 'Trades',
    'tabs.analytics': 'Analysen',
    'tabs.health': 'Gesundheit',
    'tabs.settings': 'Einstellungen',
    'tabs.weights': 'Signalgewichte',
    'tabs.performance': 'Performance',
    'tabs.runOptimizer': 'Optimizer starten',
    'tabs.viewResults': 'Ergebnisse ansehen',
    'finance.title': 'Finanzdaten',
    'finance.subtitle': 'Finanzdaten und Marktintelligenz in Echtzeit',
    'finance.lastUpdate': 'Letztes Update',
    'finance.refresh': 'Aktualisieren',
    'finance.overview': 'Übersicht',
    'finance.crypto': 'Krypto',
    'finance.stocks': 'Aktien',
    'finance.bybit': 'Bybit Orderbuch',
    'finance.binance': 'Binance Orderbuch',
    'finance.economic': 'Makro',
    'finance.news': 'News',
    'finance.btcPrice': 'BTC Preis',
    'finance.ethPrice': 'ETH Preis',
    'finance.fedFundsRate': 'Fed Funds Rate',
    'finance.latestNews': 'Neueste News',
    'finance.today': 'Heute',
    'finance.topCrypto': 'Top Krypto',
    'finance.bybitPreview': 'Bybit Orderbuch',
    'finance.binancePreview': 'Binance Orderbuch',
    'finance.cryptoPrices': 'Kryptowährungskurse',
    'finance.top20ByVolume': 'Top 20 nach Volumen',
    'finance.top100ByVolume': 'Top 100 nach Volumen',
    'finance.showTop20': 'Top 20 anzeigen',
    'finance.showTop100': 'Alle 100 anzeigen',
    'finance.rank': '#',
    'finance.coin': 'Coin',
    'finance.price': 'Preis',
    'finance.change24h': '24h Veränderung',
    'finance.volume24h': 'Volumen (24h)',
    'finance.marketCap': 'Marktkapitalisierung',
    'finance.stockPrices': 'Aktienkurse',
    'finance.stockWatchlist': 'Aktien-Watchlist',
    'finance.addStock': 'Aktie hinzufügen',
    'finance.add': 'Hinzufügen',
    'finance.remove': 'Entfernen',
    'finance.actions': 'Aktionen',
    'finance.sortAsc': 'Aufsteigend sortieren',
    'finance.sortDesc': 'Absteigend sortieren',
    'finance.openArticle': 'Artikel öffnen',
    'finance.markRead': 'Als gelesen markieren',
    'finance.read': 'Gelesen',
    'finance.unread': 'Ungelesen',
    'finance.symbol': 'Symbol',
    'finance.name': 'Name',
    'finance.sector': 'Sektor',
    'finance.change': 'Veränderung',
    'finance.volume': 'Volumen',
    'finance.bybitTitle': 'Bybit Orderbuchdaten',
    'finance.binanceTitle': 'Binance Orderbuchdaten',
    'finance.live': 'Live',
    'finance.midPrice': 'Mid Price',
    'finance.spread': 'Spread',
    'finance.bidDepth': 'Bid Tiefe',
    'finance.askDepth': 'Ask Tiefe',
    'finance.imbalance': 'Imbalance',
    'finance.economicIndicators': 'Wirtschaftsindikatoren',
    'finance.latestNewsTitle': 'Neueste News',
    'finance.newsSortedHint': 'Neueste Meldungen stehen immer oben.',
    'finance.loadingBots': 'Bots werden geladen...',
    'pairlist.title': 'Pairlist Optimizer',
    'pairlist.subtitle': 'Erzeuge optimale Pairlists mit ML-Training oder umfassenden Backtests',
    'layout.openMenu': 'Navigationsmenü öffnen',
    'settings.title': 'Einstellungen',
    'settings.general': 'Allgemeine Einstellungen',
    'settings.discovery': 'Discovery-Einstellungen',
    'settings.infrastructure': 'Infrastruktur',
    'settings.changePassword': 'Passwort ändern',
    'settings.currentPassword': 'Aktuelles Passwort',
    'settings.newPassword': 'Neues Passwort',
    'settings.confirmPassword': 'Passwort bestätigen',
    'settings.passwordChanged': 'Passwort erfolgreich geändert',
    'settings.passwordFailed': 'Passwort konnte nicht geändert werden',
    'settings.currentPasswordRequired': 'Aktuelles Passwort ist erforderlich',
    'settings.passwordMismatch': 'Passwörter stimmen nicht überein',
    'settings.passwordWeak': 'Passwort erfüllt die Anforderungen nicht',
    'settings.refreshInterval': 'Aktualisierungsintervall',
    'settings.refreshIntervalHint': 'Dashboard-Aktualisierung in Sekunden',
    'settings.hostIp': 'Host-IP oder DNS',
    'settings.apiUsername': 'API-Benutzername',
    'settings.apiPassword': 'API-Passwort',
    'settings.analyticsDbUrl': 'Analytics-Datenbank-URL',
    'settings.financeDbHost': 'Finance-DB Host',
    'settings.financeDbPort': 'Finance-DB Port',
    'settings.financeDbUser': 'Finance-DB Benutzer',
    'settings.financeDbPassword': 'Finance-DB Passwort',
    'settings.financeDbName': 'Finance-DB Name',
    'settings.fredApiKey': 'FRED API Key',
    'settings.newsApiKey': 'News API Key',
    'settings.appRootDir': 'App Root Verzeichnis',
    'settings.freqtradeRootDir': 'Freqtrade Root Verzeichnis',
    'settings.freqtradeDataDir': 'Freqtrade Datenverzeichnis',
    'settings.agentRootDir': 'Agent Root Verzeichnis',
    'settings.saved': 'Gespeichert',
    'settings.saveFailed': 'Speichern fehlgeschlagen',
    'settings.languageHint': 'Wählt die Sprache für Setup, Navigation und Einstellungen.',
    'dashboard.botsSection': 'Bots',
    'dashboard.portfolioOverview': 'Portfolio-Übersicht',
    'dashboard.totalProfit': 'Gesamtprofit',
    'dashboard.totalBalance': 'Gesamtkontostand',
    'dashboard.openPositions': 'Offene Positionen',
    'dashboard.avgWinRate': 'Durchschnittliche Gewinnrate',
    'dashboard.tradingBots': 'Trading-Bots',
    'dashboard.withData': 'mit Daten',
    'dashboard.bestPerformer': 'Bester Performer',
    'dashboard.worstPerformer': 'Schlechtester Performer',
    'dashboard.byExchange': 'Nach Exchange',
    'dashboard.byStrategy': 'Nach Strategie',
    'dashboard.positions': 'Positionen',
    'dashboard.failedPortfolioSummary': 'Portfolio-Zusammenfassung konnte nicht geladen werden',
    'dashboard.failedExchangeBreakdown': 'Exchange-Aufschlüsselung konnte nicht geladen werden',
    'dashboard.failedStrategyBreakdown': 'Strategie-Aufschlüsselung konnte nicht geladen werden',
    'dashboard.searchBots': 'Bots suchen...',
    'dashboard.allEnvironments': 'Alle Umgebungen',
    'dashboard.allHealthStates': 'Alle Gesundheitszustände',
    'dashboard.healthy': 'Gesund',
    'dashboard.degraded': 'Beeinträchtigt',
    'dashboard.unreachable': 'Nicht erreichbar',
    'dashboard.sortByName': 'Nach Name sortieren',
    'dashboard.sortByProfit': 'Nach Profit sortieren',
    'dashboard.sortByHealth': 'Nach Gesundheit sortieren',
    'dashboard.sortByExchange': 'Nach Exchange sortieren',
    'dashboard.sortByStrategy': 'Nach Strategie sortieren',
    'dashboard.ascending': 'Aufsteigend',
    'dashboard.descending': 'Absteigend',
    'dashboard.gridView': 'Rasteransicht',
    'dashboard.listView': 'Listenansicht',
    'dashboard.filters': 'Filter',
    'dashboard.hideFilters': 'Filter ausblenden',
    'dashboard.moreFilters': 'Mehr Filter',
    'historic.title': 'Historische Daten',
    'historic.subtitle': 'Aus der konfigurierten Analytics-Datenbank',
    'historic.totalBots': 'Bots gesamt',
    'historic.totalTrades': 'Trades gesamt',
    'historic.openTrades': 'Offene Trades',
    'historic.totalProfit': 'Gesamtprofit',
    'historic.botSnapshots': 'Bot-Snapshots',
    'historic.botName': 'Bot-Name',
    'historic.lastUpdate': 'Letztes Update',
    'historic.profitAll': 'Profit gesamt',
    'historic.action': 'Aktion',
    'historic.noDataHint': 'Keine historischen Daten verfügbar. Stelle sicher, dass die Analytics-Pipeline läuft.',
    'historic.profitHistory': 'Profit-Verlauf',
    'historic.winrateHistory': 'Gewinnraten-Verlauf',
    'historic.aboutTitle': 'Über historische Daten',
    'historic.aboutBody': 'Diese Daten stammen aus der konfigurierten Analytics-Datenbank, die historische Snapshots deiner Trading-Bots speichert. Die Pipeline läuft alle 5 Minuten, um Bot-Metriken zu erfassen. Klicke auf eine Bot-Zeile oder "Chart ansehen", um Profit- und Gewinnraten-Verläufe über die Zeit zu sehen.',
    'compare.title': 'Bots vergleichen',
    'compare.clearAll': 'Alles löschen',
    'compare.selectBots': 'Bots zum Vergleichen auswählen',
    'compare.selectedCount': 'ausgewählt',
    'compare.searchPlaceholder': 'Bots nach Name, Exchange oder Strategie suchen...',
    'compare.performanceComparison': 'Performance-Vergleich',
    'compare.metric': 'Metrik',
    'compare.dry': 'Dry',
    'compare.totalTrades': 'Trades gesamt',
    'compare.status': 'Status',
    'compare.noSelection': 'Wähle mindestens zwei Bots für einen Vergleich aus.',
    'compare.totalProfit': 'Gesamtprofit',
    'compare.totalProfitPct': 'Gesamtprofit %',
    'compare.avgDuration': 'Ø Dauer',
    'compare.bestValue': 'Bester Wert',
    'compare.emptyDescription': 'Wähle oben bis zu 5 Bots aus, um ihre Performance direkt nebeneinander zu vergleichen.',
    'discovery.title': 'Bot-Discovery',
    'discovery.subtitle': 'Automatische und manuelle Bot-Discovery verwalten',
    'discovery.addManually': 'Bot manuell hinzufügen',
    'discovery.scanning': 'Scan läuft...',
    'discovery.scanCompleted': 'Scan erfolgreich abgeschlossen',
    'discovery.discovered': 'Gefunden',
    'discovery.new': 'Neu',
    'discovery.updated': 'Aktualisiert',
    'discovery.removed': 'Entfernt',
    'discovery.registeredBots': 'Registrierte Bots',
    'discovery.botNameRequired': 'Bot-Name ist erforderlich',
    'discovery.apiUrlRequired': 'API-URL ist erforderlich',
    'discovery.failedAddBot': 'Bot konnte nicht hinzugefügt werden',
    'discovery.failedDeleteBot': 'Bot konnte nicht gelöscht werden',
    'discovery.discoveryStatus': 'Discovery-Status',
    'alerts.summary': 'gesamt, ungelesen',
    'alerts.markAllRead': 'Alle als gelesen markieren',
    'alerts.severity': 'Schweregrad',
    'alerts.all': 'Alle',
    'alerts.critical': 'Kritisch',
    'alerts.warning': 'Warnung',
    'alerts.info': 'Info',
    'alerts.unreadOnly': 'Nur ungelesen',
    'alerts.read': 'Gelesen',
    'alerts.selected': 'ausgewählt',
    'alerts.markRead': 'Als gelesen markieren',
    'alerts.dismiss': 'Verwerfen',
    'backtest.title': 'Backtest-Ergebnisse',
    'backtest.subtitle': 'Aus der SQLite-zu-PostgreSQL-Migration',
    'backtest.strategies': 'Strategien',
    'backtest.profitable': 'Profitabel',
    'backtest.unprofitable': 'Nicht profitabel',
    'backtest.avgProfit': 'Ø Profit',
    'backtest.profitComparison': 'Profit-Vergleich',
    'backtest.noData': 'Keine Daten verfügbar',
    'backtest.sortProfit': 'Profit',
    'backtest.sortTrades': 'Trades',
    'backtest.sortWinrate': 'Gewinnrate',
    'backtest.sortDrawdown': 'Drawdown',
    'backtest.strategyResults': 'Strategie-Ergebnisse',
    'backtest.rank': 'Rang',
    'backtest.timeframe': 'Zeitrahmen',
    'backtest.bestPair': 'Bestes Pair',
    'backtest.loadingResults': 'Backtest-Ergebnisse werden geladen...',
    'backtest.noResults': 'Keine Backtest-Ergebnisse gefunden.',
    'backtest.bug': 'BUG?',
    'backtest.aboutTitle': 'Über Backtest-Ergebnisse',
    'backtest.aboutBody': 'Diese Ergebnisse stammen aus importierten Backtests und werden jetzt in der PostgreSQL-Datenbank des Dashboards gespeichert. Extrem hohe Werte können auf fehlerhafte Positionsgrößen oder fehlerhafte Quelldaten hinweisen und sollten vor einer echten Nutzung geprüft werden.',
    'users.title': 'Benutzerverwaltung',
    'users.addUser': 'Benutzer hinzufügen',
    'users.username': 'Benutzername',
    'users.role': 'Rolle',
    'users.created': 'Erstellt',
    'users.failedLoad': 'Benutzer konnten nicht geladen werden. Stelle sicher, dass du Admin-Zugriff hast.',
    'users.createdSuccess': 'Benutzer erfolgreich erstellt',
    'users.updatedSuccess': 'Benutzer erfolgreich aktualisiert',
    'users.deletedSuccess': 'Benutzer erfolgreich gelöscht',
    'users.failedCreate': 'Benutzer konnte nicht erstellt werden',
    'users.failedUpdate': 'Benutzer konnte nicht aktualisiert werden',
    'users.failedDelete': 'Benutzer konnte nicht gelöscht werden',
    'users.required': 'Benutzername und Passwort sind erforderlich',
    'users.noChanges': 'Keine Änderungen zum Speichern',
    'users.noneFound': 'Keine Benutzer gefunden',
    'freqtradeBots.loading': 'Bots werden geladen...',
    'freqtradeBots.noneFound': 'Keine Bots mit API-Zugang gefunden.',
    'freqtradeBots.checkConfig': 'Prüfe Discovery und API-Konfiguration.',
    'freqtradeBots.count': 'Bots mit API-Zugang',
    'freqtradeBots.openDirect': 'Direkt öffnen',
    'freqtradeBots.loadingBot': 'Wird geladen',
    'strategyLab.title': 'Strategy Lab',
    'strategyLab.subtitle': 'Automatisierte Strategieoptimierung und Workflow-Steuerung',
    'strategyLab.uploading': 'Lädt hoch...',
    'strategyLab.uploadStrategy': 'Strategie hochladen',
    'strategyLab.uploadedSuccessfully': 'erfolgreich hochgeladen.',
    'strategyLab.uploadFailed': 'Upload fehlgeschlagen.',
    'strategyLab.completedRuns': 'Abgeschlossene Runs',
    'strategyLab.activeWorkflows': 'Aktive Workflows',
    'strategyLab.strategyList': 'Strategieliste',
    'strategyLab.strategyListDesc': 'Alle Strategien mit Metadaten und Indikatoren durchsuchen',
    'strategyLab.workflowControl': 'Workflow-Steuerung',
    'strategyLab.workflowControlDesc': 'Automatisiere: stoppen → herunterladen → backtesten → hyperopten → deployen',
    'strategyLab.hyperoptMonitor': 'Hyperopt-Monitor',
    'strategyLab.hyperoptMonitorDesc': 'Live-Epoch-Tracking und Parameter-Extraktion',
    'strategyLab.optimizationResults': 'Optimierungsergebnisse',
    'strategyLab.optimizationResultsDesc': 'V1 → V2 → V3 Verbesserungen und Gewinner-Konfigurationen verfolgen',
    'strategyLab.recentActivity': 'Letzte Aktivität',
    'setup.connectTitle': 'Dashboard verbinden',
    'setup.connectDescription': 'Für gebündelte mobile Installationen gib hier die URL ein, unter der dieses Dashboard erreichbar ist.',
    'setup.dashboardUrl': 'Dashboard-URL',
    'setup.saveContinue': 'Speichern und weiter',
    'setup.clearNotice': 'Gespeichertes Backend gelöscht. Bitte neue Adresse eintragen.',
    'setup.urlRequired': 'Bitte gib die Dashboard-URL ein.',
    'setup.urlSchemeRequired': 'Die URL muss mit http:// oder https:// beginnen.',
    'setup.title': 'Ersteinrichtung',
    'setup.description': 'Konfiguriere die wichtigsten Werte einmal, damit Discovery, Analytics, Finanzdaten und Strategietools sauber funktionieren.',
    'setup.loadingStatus': 'Setup-Status wird geladen...',
    'setup.missingRequired': 'Fehlende Pflichtwerte',
    'setup.language': 'Sprache',
    'setup.discoveryHost': 'Discovery Host IP',
    'setup.botApiUsername': 'Bot API Benutzername',
    'setup.botApiPassword': 'Bot API Passwort',
    'setup.refreshInterval': 'Aktualisierungsintervall',
    'setup.infrastructure': 'Infrastruktur',
    'setup.infrastructureHint': 'Diese Werte machen die App ohne Code-Änderungen auf andere Server übertragbar.',
    'setup.theme': 'Design',
    'setup.save': 'Setup speichern',
    'setup.saving': 'Speichert...',
    'setup.skip': 'Vorläufig überspringen',
    'setup.saved': 'Setup gespeichert. Du kannst jetzt zum Dashboard wechseln.',
    'setup.seconds15': '15 Sekunden',
    'setup.seconds30': '30 Sekunden',
    'setup.seconds60': '60 Sekunden',
    'setup.seconds120': '120 Sekunden',
    'theme.classic': 'Klassisch',
    'theme.industrial': 'Industrial',
    'language.german': 'Deutsch',
    'language.english': 'Englisch',
    'help.discovery_host_ip': 'Hostname oder IP, unter der das Dashboard die Freqtrade-Bot-APIs erreicht. Wenn dieser Wert falsch ist, werden Bots zwar entdeckt, aber liefern keine Daten.',
    'help.api_username': 'Standard-Benutzername für die Freqtrade REST-API. Dieser wird für Discovery, Health-Checks, Metriken und Vergleiche verwendet.',
    'help.api_password': 'Passendes Passwort für die Freqtrade REST-API. Ohne gültige Zugangsdaten bleiben Bot-Status, Trades und Kennzahlen leer.',
    'help.refresh_interval': 'Steuert, wie oft das UI Daten neu anfordert. 30 Sekunden ist ein guter Alltag-Standard.',
    'help.analytics_db_url': 'Vollständige PostgreSQL-Verbindungszeichenkette für historische Analytics-Daten. Wird für Historic, Compare und Langzeit-Auswertungen genutzt.',
    'help.finance_db_host': 'Host des Finance-Datenbankservers. Diese Datenbank speichert News, Aktien- und Makrodaten.',
    'help.finance_db_port': 'Port der Finance-Datenbank. Bei PostgreSQL ist das meist 5432.',
    'help.finance_db_user': 'Datenbank-Benutzer für den Zugriff auf die Finance-Datenbank.',
    'help.finance_db_password': 'Passwort für den Finance-Datenbank-Benutzer. Nur nötig, wenn die Finance-Module auf eine getrennte DB schreiben und lesen.',
    'help.finance_db_name': 'Name der Finance-Datenbank, in der News, Aktienkurse und Wirtschaftsindikatoren gespeichert werden.',
    'help.app_root_dir': 'Stammverzeichnis dieses Dashboards auf dem Server. Wird für Scripts, Ergebnisse und Importe verwendet.',
    'help.freqtrade_root_dir': 'Basisverzeichnis deiner Freqtrade-Installation oder Bot-Projekte. Strategy Lab und Importe suchen hier nach Strategien und Konfigurationen.',
    'help.freqtrade_data_dir': 'Verzeichnis mit user_data, Backtests oder Pairlist-Ergebnissen. Wenn der Pfad falsch ist, bleiben Importe oft leer.',
    'help.freqtrade_agent_root_dir': 'Pfad zur Agent- oder Automations-Umgebung, falls du das Agent-Modul verwendest.',
    'help.fred_api_key': 'Optional für echte Makrodaten. Ohne Schlüssel zeigt das Tool Demo-Werte. Den API-Key bekommst du in den offiziellen FRED API Docs.',
    'help.newsapi_key': 'Optional für zusätzliche Markt- und Business-News. Ohne Schlüssel nutzt das Tool nur öffentliche Crypto-News und Fallback-Daten. Den Key bekommst du im offiziellen News API Get Started.',
    'help.fred_link_label': 'FRED API Keys',
    'help.news_link_label': 'News API Get Started',
  },
  en: {
    'common.save': 'Save',
    'common.saveAll': 'Save All',
    'common.saving': 'Saving...',
    'common.loading': 'Loading...',
    'common.clear': 'Clear',
    'common.menu': 'Menu',
    'common.logout': 'Logout',
    'common.language': 'Language',
    'common.theme': 'Theme',
    'common.system': 'System',
    'common.add': 'Add',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.actions': 'Actions',
    'common.search': 'Search',
    'common.status': 'Status',
    'common.loadingData': 'Loading data...',
    'common.noData': 'No data available',
    'common.notAvailable': 'N/A',
    'common.online': 'Online',
    'common.offline': 'Offline',
    'common.view': 'View',
    'common.never': 'Never',
    'common.lastUpdate': 'Last update',
    'common.balance': 'Balance',
    'common.profit': 'Profit',
    'common.strategy': 'Strategy',
    'common.exchange': 'Exchange',
    'common.bots': 'Bots',
    'common.trades': 'Trades',
    'common.open': 'Open',
    'common.winRate': 'Win Rate',
    'common.environment': 'Environment',
    'common.refreshing': 'Refreshing...',
    'common.scanNow': 'Scan Now',
    'common.sortBy': 'Sort by',
    'common.hide': 'Hide',
    'common.more': 'More',
    'nav.dashboard': 'Dashboard',
    'nav.freqtradeBots': 'Freqtrade Bots',
    'nav.historicBots': 'Historic Bots',
    'nav.financialData': 'Financial Data',
    'nav.strategyLab': 'Strategy Lab',
    'nav.pairlistOptimizer': 'Pairlist Optimizer',
    'nav.backtestResults': 'Backtest Results',
    'nav.compare': 'Compare',
    'nav.discovery': 'Discovery',
    'nav.alerts': 'Alerts',
    'nav.settings': 'Settings',
    'nav.users': 'Users',
    'tabs.overview': 'Overview',
    'tabs.trades': 'Trades',
    'tabs.analytics': 'Analytics',
    'tabs.health': 'Health',
    'tabs.settings': 'Settings',
    'tabs.weights': 'Signal Weights',
    'tabs.performance': 'Performance',
    'tabs.runOptimizer': 'Run Optimizer',
    'tabs.viewResults': 'View Results',
    'finance.title': 'Finance Data',
    'finance.subtitle': 'Real-time financial data and market intelligence',
    'finance.lastUpdate': 'Last update',
    'finance.refresh': 'Refresh',
    'finance.overview': 'Overview',
    'finance.crypto': 'Crypto',
    'finance.stocks': 'Stocks',
    'finance.bybit': 'Bybit Orderbook',
    'finance.binance': 'Binance Orderbook',
    'finance.economic': 'Economic',
    'finance.news': 'News',
    'finance.btcPrice': 'BTC Price',
    'finance.ethPrice': 'ETH Price',
    'finance.fedFundsRate': 'Fed Funds Rate',
    'finance.latestNews': 'Latest News',
    'finance.today': 'Today',
    'finance.topCrypto': 'Top Crypto',
    'finance.bybitPreview': 'Bybit Orderbook',
    'finance.binancePreview': 'Binance Orderbook',
    'finance.cryptoPrices': 'Cryptocurrency Prices',
    'finance.top20ByVolume': 'Top 20 by volume',
    'finance.top100ByVolume': 'Top 100 by volume',
    'finance.showTop20': 'Show Top 20',
    'finance.showTop100': 'Show All 100',
    'finance.rank': '#',
    'finance.coin': 'Coin',
    'finance.price': 'Price',
    'finance.change24h': '24h Change',
    'finance.volume24h': 'Volume (24h)',
    'finance.marketCap': 'Market Cap',
    'finance.stockPrices': 'Stock Prices',
    'finance.stockWatchlist': 'Stock Watchlist',
    'finance.addStock': 'Add Stock',
    'finance.add': 'Add',
    'finance.remove': 'Remove',
    'finance.actions': 'Actions',
    'finance.sortAsc': 'Sort ascending',
    'finance.sortDesc': 'Sort descending',
    'finance.openArticle': 'Open article',
    'finance.markRead': 'Mark as read',
    'finance.read': 'Read',
    'finance.unread': 'Unread',
    'finance.symbol': 'Symbol',
    'finance.name': 'Name',
    'finance.sector': 'Sector',
    'finance.change': 'Change',
    'finance.volume': 'Volume',
    'finance.bybitTitle': 'Bybit Orderbook Data',
    'finance.binanceTitle': 'Binance Orderbook Data',
    'finance.live': 'Live',
    'finance.midPrice': 'Mid Price',
    'finance.spread': 'Spread',
    'finance.bidDepth': 'Bid Depth',
    'finance.askDepth': 'Ask Depth',
    'finance.imbalance': 'Imbalance',
    'finance.economicIndicators': 'Economic Indicators',
    'finance.latestNewsTitle': 'Latest News',
    'finance.newsSortedHint': 'Newest items are always shown first.',
    'finance.loadingBots': 'Loading bots...',
    'pairlist.title': 'Pairlist Optimizer',
    'pairlist.subtitle': 'Generate optimal pairlists using ML training or comprehensive backtests',
    'layout.openMenu': 'Open navigation menu',
    'settings.title': 'Settings',
    'settings.general': 'General Settings',
    'settings.discovery': 'Discovery Settings',
    'settings.infrastructure': 'Infrastructure',
    'settings.changePassword': 'Change Password',
    'settings.currentPassword': 'Current Password',
    'settings.newPassword': 'New Password',
    'settings.confirmPassword': 'Confirm Password',
    'settings.passwordChanged': 'Password changed successfully',
    'settings.passwordFailed': 'Failed to change password',
    'settings.currentPasswordRequired': 'Current password is required',
    'settings.passwordMismatch': 'Passwords do not match',
    'settings.passwordWeak': 'Password does not meet requirements',
    'settings.refreshInterval': 'Refresh Interval',
    'settings.refreshIntervalHint': 'Dashboard refresh interval in seconds',
    'settings.hostIp': 'Host IP or DNS',
    'settings.apiUsername': 'API Username',
    'settings.apiPassword': 'API Password',
    'settings.analyticsDbUrl': 'Analytics Database URL',
    'settings.financeDbHost': 'Finance DB Host',
    'settings.financeDbPort': 'Finance DB Port',
    'settings.financeDbUser': 'Finance DB User',
    'settings.financeDbPassword': 'Finance DB Password',
    'settings.financeDbName': 'Finance DB Name',
    'settings.fredApiKey': 'FRED API Key',
    'settings.newsApiKey': 'News API Key',
    'settings.appRootDir': 'App Root Directory',
    'settings.freqtradeRootDir': 'Freqtrade Root Directory',
    'settings.freqtradeDataDir': 'Freqtrade Data Directory',
    'settings.agentRootDir': 'Agent Root Directory',
    'settings.saved': 'Saved',
    'settings.saveFailed': 'Save failed',
    'settings.languageHint': 'Controls the language used for setup, navigation, and settings.',
    'dashboard.botsSection': 'Bots',
    'dashboard.portfolioOverview': 'Portfolio Overview',
    'dashboard.totalProfit': 'Total Profit',
    'dashboard.totalBalance': 'Total Balance',
    'dashboard.openPositions': 'Open Positions',
    'dashboard.avgWinRate': 'Avg Win Rate',
    'dashboard.tradingBots': 'Trading Bots',
    'dashboard.withData': 'with data',
    'dashboard.bestPerformer': 'Best Performer',
    'dashboard.worstPerformer': 'Worst Performer',
    'dashboard.byExchange': 'By Exchange',
    'dashboard.byStrategy': 'By Strategy',
    'dashboard.positions': 'Positions',
    'dashboard.failedPortfolioSummary': 'Failed to load portfolio summary',
    'dashboard.failedExchangeBreakdown': 'Failed to load exchange breakdown',
    'dashboard.failedStrategyBreakdown': 'Failed to load strategy breakdown',
    'dashboard.searchBots': 'Search bots...',
    'dashboard.allEnvironments': 'All Environments',
    'dashboard.allHealthStates': 'All Health States',
    'dashboard.healthy': 'Healthy',
    'dashboard.degraded': 'Degraded',
    'dashboard.unreachable': 'Unreachable',
    'dashboard.sortByName': 'Sort by Name',
    'dashboard.sortByProfit': 'Sort by Profit',
    'dashboard.sortByHealth': 'Sort by Health',
    'dashboard.sortByExchange': 'Sort by Exchange',
    'dashboard.sortByStrategy': 'Sort by Strategy',
    'dashboard.ascending': 'Ascending',
    'dashboard.descending': 'Descending',
    'dashboard.gridView': 'Grid view',
    'dashboard.listView': 'List view',
    'dashboard.filters': 'Filters',
    'dashboard.hideFilters': 'Hide Filters',
    'dashboard.moreFilters': 'More Filters',
    'historic.title': 'Historic Data',
    'historic.subtitle': 'From configured analytics database',
    'historic.totalBots': 'Total Bots',
    'historic.totalTrades': 'Total Trades',
    'historic.openTrades': 'Open Trades',
    'historic.totalProfit': 'Total Profit',
    'historic.botSnapshots': 'Bot Snapshots',
    'historic.botName': 'Bot Name',
    'historic.lastUpdate': 'Last Update',
    'historic.profitAll': 'Profit All',
    'historic.action': 'Action',
    'historic.noDataHint': 'No historic data available. Make sure the analytics pipeline is running.',
    'historic.profitHistory': 'Profit History',
    'historic.winrateHistory': 'Winrate History',
    'historic.aboutTitle': 'About Historic Data',
    'historic.aboutBody': 'This data comes from the configured analytics database, which stores historical snapshots of your trading bots. The pipeline runs every 5 minutes to capture bot metrics. Click a bot row or "View Chart" to see profit and winrate history over time.',
    'compare.title': 'Compare Bots',
    'compare.clearAll': 'Clear All',
    'compare.selectBots': 'Select Bots to Compare',
    'compare.selectedCount': 'selected',
    'compare.searchPlaceholder': 'Search bots by name, exchange, or strategy...',
    'compare.performanceComparison': 'Performance Comparison',
    'compare.metric': 'Metric',
    'compare.dry': 'Dry',
    'compare.totalTrades': 'Total Trades',
    'compare.status': 'Status',
    'compare.noSelection': 'Select at least two bots to compare them.',
    'compare.totalProfit': 'Total Profit',
    'compare.totalProfitPct': 'Total Profit %',
    'compare.avgDuration': 'Avg Duration',
    'compare.bestValue': 'Best performing value',
    'compare.emptyDescription': 'Choose up to 5 bots above to see their performance side by side.',
    'discovery.title': 'Bot Discovery',
    'discovery.subtitle': 'Manage automatic and manual bot discovery',
    'discovery.addManually': 'Add Bot Manually',
    'discovery.scanning': 'Scanning...',
    'discovery.scanCompleted': 'Scan Completed Successfully',
    'discovery.discovered': 'Discovered',
    'discovery.new': 'New',
    'discovery.updated': 'Updated',
    'discovery.removed': 'Removed',
    'discovery.registeredBots': 'Registered Bots',
    'discovery.botNameRequired': 'Bot name is required',
    'discovery.apiUrlRequired': 'API URL is required',
    'discovery.failedAddBot': 'Failed to add bot',
    'discovery.failedDeleteBot': 'Failed to delete bot',
    'discovery.discoveryStatus': 'Discovery Status',
    'alerts.summary': 'total alerts, unread',
    'alerts.markAllRead': 'Mark all as read',
    'alerts.severity': 'Severity',
    'alerts.all': 'All',
    'alerts.critical': 'Critical',
    'alerts.warning': 'Warning',
    'alerts.info': 'Info',
    'alerts.unreadOnly': 'Unread only',
    'alerts.read': 'Read',
    'alerts.selected': 'selected',
    'alerts.markRead': 'Mark Read',
    'alerts.dismiss': 'Dismiss',
    'backtest.title': 'Backtest Results',
    'backtest.subtitle': 'From SQLite to PostgreSQL migration',
    'backtest.strategies': 'Strategies',
    'backtest.profitable': 'Profitable',
    'backtest.unprofitable': 'Unprofitable',
    'backtest.avgProfit': 'Avg Profit',
    'backtest.profitComparison': 'Profit Comparison',
    'backtest.noData': 'No data available',
    'backtest.sortProfit': 'Profit',
    'backtest.sortTrades': 'Trades',
    'backtest.sortWinrate': 'Win Rate',
    'backtest.sortDrawdown': 'Drawdown',
    'backtest.strategyResults': 'Strategy Results',
    'backtest.rank': 'Rank',
    'backtest.timeframe': 'Timeframe',
    'backtest.bestPair': 'Best Pair',
    'backtest.loadingResults': 'Loading backtest results...',
    'backtest.noResults': 'No backtest results found.',
    'backtest.bug': 'BUG?',
    'backtest.aboutTitle': 'About Backtest Results',
    'backtest.aboutBody': 'These results come from imported backtests and are now stored in the dashboard PostgreSQL database. Extremely high values can indicate faulty position sizing or bad source data and should be reviewed before real use.',
    'users.title': 'User Management',
    'users.addUser': 'Add User',
    'users.username': 'Username',
    'users.role': 'Role',
    'users.created': 'Created',
    'users.failedLoad': 'Failed to load users. Make sure you have admin access.',
    'users.createdSuccess': 'User created successfully',
    'users.updatedSuccess': 'User updated successfully',
    'users.deletedSuccess': 'User deleted successfully',
    'users.failedCreate': 'Failed to create user',
    'users.failedUpdate': 'Failed to update user',
    'users.failedDelete': 'Failed to delete user',
    'users.required': 'Username and password are required',
    'users.noChanges': 'No changes to save',
    'users.noneFound': 'No users found',
    'freqtradeBots.loading': 'Loading bots...',
    'freqtradeBots.noneFound': 'No bots with API access found.',
    'freqtradeBots.checkConfig': 'Check discovery and API configuration.',
    'freqtradeBots.count': 'bots with API access',
    'freqtradeBots.openDirect': 'Open Direct',
    'freqtradeBots.loadingBot': 'Loading',
    'strategyLab.title': 'Strategy Lab',
    'strategyLab.subtitle': 'Automated strategy optimization and workflow management',
    'strategyLab.uploading': 'Uploading...',
    'strategyLab.uploadStrategy': 'Upload Strategy',
    'strategyLab.uploadedSuccessfully': 'uploaded successfully.',
    'strategyLab.uploadFailed': 'Upload failed.',
    'strategyLab.completedRuns': 'Completed Runs',
    'strategyLab.activeWorkflows': 'Active Workflows',
    'strategyLab.strategyList': 'Strategy List',
    'strategyLab.strategyListDesc': 'Browse all strategies with metadata and indicators',
    'strategyLab.workflowControl': 'Workflow Control',
    'strategyLab.workflowControlDesc': 'Automate: stop -> download -> backtest -> hyperopt -> deploy',
    'strategyLab.hyperoptMonitor': 'Hyperopt Monitor',
    'strategyLab.hyperoptMonitorDesc': 'Real-time epoch tracking and parameter extraction',
    'strategyLab.optimizationResults': 'Optimization Results',
    'strategyLab.optimizationResultsDesc': 'Track V1 -> V2 -> V3 improvements and winning configs',
    'strategyLab.recentActivity': 'Recent Activity',
    'setup.connectTitle': 'Connect Dashboard',
    'setup.connectDescription': 'For bundled mobile installs, enter the URL where this dashboard is reachable.',
    'setup.dashboardUrl': 'Dashboard URL',
    'setup.saveContinue': 'Save and Continue',
    'setup.clearNotice': 'Cleared saved backend. Enter a new one.',
    'setup.urlRequired': 'Please enter your dashboard URL.',
    'setup.urlSchemeRequired': 'The URL must start with http:// or https://.',
    'setup.title': 'First-Time Setup',
    'setup.description': 'Configure the essentials once so discovery, analytics, financial feeds, and strategy tools work reliably.',
    'setup.loadingStatus': 'Loading setup status...',
    'setup.missingRequired': 'Missing required settings',
    'setup.language': 'Language',
    'setup.discoveryHost': 'Discovery Host IP',
    'setup.botApiUsername': 'Bot API Username',
    'setup.botApiPassword': 'Bot API Password',
    'setup.refreshInterval': 'Refresh Interval',
    'setup.infrastructure': 'Infrastructure',
    'setup.infrastructureHint': 'These values make the app portable to another server without code changes.',
    'setup.theme': 'Theme',
    'setup.save': 'Save Setup',
    'setup.saving': 'Saving...',
    'setup.skip': 'Skip for now',
    'setup.saved': 'Setup saved. You can continue to the dashboard.',
    'setup.seconds15': '15 seconds',
    'setup.seconds30': '30 seconds',
    'setup.seconds60': '60 seconds',
    'setup.seconds120': '120 seconds',
    'theme.classic': 'Classic',
    'theme.industrial': 'Industrial',
    'language.german': 'German',
    'language.english': 'English',
    'help.discovery_host_ip': 'Hostname or IP address the dashboard should use to reach Freqtrade bot APIs. If this is wrong, bots may be discovered but still show no data.',
    'help.api_username': 'Default username for the Freqtrade REST API. Used for discovery, health checks, metrics, and comparisons.',
    'help.api_password': 'Matching password for the Freqtrade REST API. Without valid credentials, bot status, trades, and metrics stay empty.',
    'help.refresh_interval': 'Controls how often the UI requests fresh data. 30 seconds is a solid day-to-day default.',
    'help.analytics_db_url': 'Full PostgreSQL connection string for historical analytics data. Used by Historic, Compare, and long-range views.',
    'help.finance_db_host': 'Host of the finance database server. This database stores news, stock data, and macro indicators.',
    'help.finance_db_port': 'Port of the finance database. For PostgreSQL this is usually 5432.',
    'help.finance_db_user': 'Database user for the finance database connection.',
    'help.finance_db_password': 'Password for the finance database user. Only needed when finance modules read and write to a separate DB.',
    'help.finance_db_name': 'Name of the finance database where news, stock prices, and economic indicators are stored.',
    'help.app_root_dir': 'Root directory of this dashboard on the server. Used to resolve scripts, results, and imports reliably.',
    'help.freqtrade_root_dir': 'Base directory of your Freqtrade installation or bot projects. Strategy Lab and imports look here for strategies and configs.',
    'help.freqtrade_data_dir': 'Directory containing user_data, backtests, or pairlist results. If this path is wrong, imports often stay empty.',
    'help.freqtrade_agent_root_dir': 'Path to the agent or automation environment if you use the Agent module.',
    'help.fred_api_key': 'Optional for real macro data. Without a key the tool shows demo values. You can get the key from the official FRED API docs.',
    'help.newsapi_key': 'Optional for additional market and business news. Without a key the tool uses public crypto news and fallback data. You can get the key from the official News API getting started docs.',
    'help.fred_link_label': 'FRED API Keys',
    'help.news_link_label': 'News API Get Started',
  },
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function detectInitialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'de' || stored === 'en') {
    return stored;
  }
  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(detectInitialLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage: setLanguageState,
    t: (key: string) => translations[language][key] || translations.en[key] || key,
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used inside I18nProvider');
  }
  return context;
}
